import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSupportAddon } from '@/hooks/useSupportAddon'
import { AddonInactive } from './shared'
import { SupportTickets } from './Tickets'
import { SupportConversations } from './Conversations'
import { SupportKnowledgeBase } from './KnowledgeBase'
import { SupportExternalUsers } from './ExternalUsers'
import { SupportSettings } from './Settings'
import { SupportAnalyticsPage } from './Analytics'

type Tab = 'tickets' | 'conversations' | 'kb' | 'users' | 'settings' | 'analytics'

// Support add-on section: tickets, knowledge base, external users,
// configuration and analytics. Only reachable when the add-on is active
// (the backend gates every /support/* route with SUPPORT_ADDON_REQUIRED).
export const SupportModule: React.FC = () => {
  const { t } = useTranslation()
  const { active, isLoading } = useSupportAddon()
  const [tab, setTab] = useState<Tab>('tickets')

  if (isLoading) {
    return <div className="p-8 text-center text-gray-400">{t('supportModule.loading')}</div>
  }

  if (!active) {
    return <AddonInactive />
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: 'tickets', label: t('supportModule.tabs.tickets') },
    { key: 'conversations', label: t('supportModule.tabs.conversations') },
    { key: 'kb', label: t('supportModule.tabs.kb') },
    { key: 'users', label: t('supportModule.tabs.users') },
    { key: 'settings', label: t('supportModule.tabs.settings') },
    { key: 'analytics', label: t('supportModule.tabs.analytics') },
  ]

  return (
    <div>
      <h1 className="text-3xl font-bold mb-8">{t('supportModule.title')}</h1>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto">
        {tabs.map((item) => (
          <button
            key={item.key}
            onClick={() => setTab(item.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              tab === item.key
                ? 'bg-[#006cd4]/20 text-[#006cd4]'
                : 'text-gray-400 hover:bg-white/5 hover:text-white'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'tickets' && <SupportTickets />}
      {tab === 'conversations' && <SupportConversations />}
      {tab === 'kb' && <SupportKnowledgeBase />}
      {tab === 'users' && <SupportExternalUsers />}
      {tab === 'settings' && <SupportSettings />}
      {tab === 'analytics' && <SupportAnalyticsPage />}
    </div>
  )
}
